/*
 * Sticky add to cart bar.
 *
 * Rather than rendering a second product form, this proxies every submission
 * through the real one in main-product. That keeps the variant, the selling
 * plan and any app-injected fields (Globo preorder, wishlist, bundles) in sync
 * for free, and means the cart notification behaves exactly as it does today.
 */
if (!customElements.get('sticky-atc')) {
  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      connectedCallback() {
        this.mainForm = document.querySelector(
          '[id^="MainProduct-"] product-form form[data-type="add-to-cart-form"]'
        );

        // No product form on the page (fully sold out, or a custom template
        // without a buy_buttons block) — there is nothing to mirror.
        if (!this.mainForm) {
          this.remove();
          return;
        }

        this.mainButton = this.mainForm.querySelector('[name="add"]');
        if (!this.mainButton) {
          this.remove();
          return;
        }

        this.buttonWrapper = this.mainForm.querySelector('.product-form__buttons') || this.mainButton;
        this.submitButton = this.querySelector('.sticky-atc__submit');
        this.submitLabel = this.querySelector('.sticky-atc__submit-label');
        this.spinner = this.querySelector('.loading-overlay__spinner');
        this.quantityInput = this.querySelector('.sticky-atc__quantity input[name="quantity"]');
        this.priceTarget = this.querySelector('.sticky-atc__price');
        this.variantTarget = this.querySelector('.sticky-atc__variant');
        this.mainPrice = document.querySelector('[id^="price-"]');

        this.syncButton();
        this.syncPrice();
        this.syncVariant();

        this.submitButton.addEventListener('click', this.onSubmit.bind(this));

        this.watchButtonState();
        this.watchVariant();
        this.watchPrice();
        this.watchVisibility();
      }

      disconnectedCallback() {
        this.observers?.forEach((observer) => observer.disconnect());
      }

      /* ------------------------------------------------------------------ */
      /* Submission                                                          */
      /* ------------------------------------------------------------------ */

      onSubmit(event) {
        event.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // Sold out, and a back-in-stock app is offering to capture the email —
        // hand off to its own button so its form, analytics and flow all run
        // exactly as they do on the main product form.
        if (this.notifyMode) {
          this.notifyTrigger()?.click();
          return;
        }

        if (this.mainButton.hasAttribute('disabled')) return;

        // Push our quantity onto the real form, submit, then put it back so the
        // product page still shows whatever the customer had chosen up there.
        const restore = this.applyQuantity();

        this.setLoading(true);
        this.preserveScroll();
        this.mainForm.requestSubmit ? this.mainForm.requestSubmit() : this.mainButton.click();

        // product-form.js builds its FormData synchronously inside the submit
        // handler, so by the next task the values have already been read.
        setTimeout(() => {
          restore();
          this.trackCompletion();
        }, 0);
      }

      /*
       * Adding to cart from a fixed bar can leave the page somewhere else
       * entirely: cart-notification.js throws on this store (the Unicorn cart
       * drawer replaced it, but product-form.js still calls it), so the add
       * finishes half-done, and whichever app takes focus afterwards drags the
       * viewport with it. Pin the scroll position for a moment so the customer
       * stays where they were, and let go the instant they scroll themselves.
       */
      preserveScroll() {
        const target = window.scrollY;
        const started = Date.now();
        let released = false;

        const release = () => {
          released = true;
          window.removeEventListener('wheel', release);
          window.removeEventListener('touchstart', release);
          window.removeEventListener('keydown', release);
        };

        window.addEventListener('wheel', release, { passive: true });
        window.addEventListener('touchstart', release, { passive: true });
        window.addEventListener('keydown', release);

        const pin = () => {
          if (released || Date.now() - started > 1200) {
            release();
            return;
          }
          // Only correct real jumps, so ordinary momentum is left alone.
          if (Math.abs(window.scrollY - target) > 150) window.scrollTo(0, target);
          requestAnimationFrame(pin);
        };

        requestAnimationFrame(pin);
      }

      applyQuantity() {
        if (!this.quantityInput) return () => {};

        const quantity = this.quantityInput.value;
        const mainQuantity = this.mainForm.querySelector('input[name="quantity"]');

        if (mainQuantity) {
          const previous = mainQuantity.value;
          mainQuantity.value = quantity;
          return () => {
            mainQuantity.value = previous;
          };
        }

        // The merchant has no quantity_selector block on the product template,
        // so add a temporary field for this one submission.
        const temporary = document.createElement('input');
        temporary.type = 'hidden';
        temporary.name = 'quantity';
        temporary.value = quantity;
        this.mainForm.appendChild(temporary);
        return () => temporary.remove();
      }

      // The real button carries .loading for the duration of the fetch; follow
      // it so the sticky spinner clears at the same moment.
      trackCompletion() {
        if (!this.mainButton.classList.contains('loading')) {
          this.setLoading(false);
          return;
        }

        const observer = new MutationObserver(() => {
          if (this.mainButton.classList.contains('loading')) return;
          this.setLoading(false);
          observer.disconnect();
        });

        observer.observe(this.mainButton, { attributes: true, attributeFilter: ['class'] });
      }

      setLoading(isLoading) {
        this.submitButton.classList.toggle('loading', isLoading);
        this.spinner?.classList.toggle('hidden', !isLoading);
        if (isLoading) {
          this.submitButton.setAttribute('aria-disabled', 'true');
        } else {
          this.submitButton.removeAttribute('aria-disabled');
        }
      }

      /* ------------------------------------------------------------------ */
      /* Mirroring                                                           */
      /* ------------------------------------------------------------------ */

      // The back-in-stock apps inject their own button into the product form.
      // Target their stable container, never their generated class names —
      // Klaviyo regenerates those every time the form is republished.
      notifyTrigger() {
        const klaviyo = document.querySelector('#klaviyo-bis-button-container button');
        if (klaviyo) return klaviyo;

        const globo = document.querySelector('#Globo-Back-In-Stock .gBackInStockBtn');
        return globo || null;
      }

      syncButton() {
        const soldOut = this.mainButton.hasAttribute('disabled');
        this.notifyMode = soldOut && this.dataset.notify === 'true' && Boolean(this.notifyTrigger());
        this.classList.toggle('sticky-atc--notify', this.notifyMode);

        if (this.notifyMode) {
          this.submitButton.removeAttribute('disabled');
          this.submitLabel.textContent = this.dataset.notifyLabel;
          return;
        }

        this.submitButton.toggleAttribute('disabled', soldOut);

        // Only follow the main label when it is saying something other than the
        // default, so a merchant's custom "Add to cart" text in the theme
        // editor is not overwritten on load.
        const mainLabel = this.mainButton.querySelector('span')?.textContent.trim();
        if (soldOut && mainLabel) {
          this.submitLabel.textContent = mainLabel;
        } else if (!soldOut) {
          this.submitLabel.textContent = this.dataset.label;
        }
      }

      watchButtonState() {
        const observer = new MutationObserver(this.syncButton.bind(this));
        observer.observe(this.mainButton, { attributes: true, attributeFilter: ['disabled'] });
        observer.observe(this.mainButton, { childList: true, subtree: true, characterData: true });

        // Klaviyo and Globo inject their buttons well after DOMContentLoaded, so
        // re-check whenever the button area changes.
        observer.observe(this.buttonWrapper, { childList: true, subtree: true });
        this.observers = [...(this.observers || []), observer];
      }

      syncVariant() {
        if (!this.variantTarget) return;

        const id = this.mainForm.querySelector('input[name="id"]')?.value;
        const variant = this.variants?.find((item) => String(item.id) === String(id));
        if (variant && !variant.title.startsWith('Default Title')) {
          this.variantTarget.textContent = variant.title;
        }
      }

      watchVariant() {
        const input = this.mainForm.querySelector('input[name="id"]');
        if (!input) return;

        // VariantSelects dispatches a bubbling change on this input whenever the
        // selection moves.
        input.addEventListener('change', () => {
          this.syncVariant();
          this.syncButton();
        });
      }

      // Show only what the customer actually pays. Dawn renders the regular and
      // the sale price in the same block and hides one with CSS, so copying the
      // markup across would surface both. Read the effective one as text.
      syncPrice() {
        if (!this.priceTarget || !this.mainPrice) return;

        const onSale = this.mainPrice.querySelector('.price--on-sale');
        const selector = onSale ? '.price-item--sale' : '.price__regular .price-item--regular';
        const price = this.mainPrice.querySelector(selector) || this.mainPrice.querySelector('.price-item');
        if (!price) return;

        const text = price.textContent.replace(/\s+/g, ' ').trim();
        if (text) this.priceTarget.textContent = text;
      }

      watchPrice() {
        if (!this.priceTarget || !this.mainPrice) return;

        const observer = new MutationObserver(this.syncPrice.bind(this));
        observer.observe(this.mainPrice, { childList: true, subtree: true, characterData: true });
        this.observers = [...(this.observers || []), observer];
      }

      get variants() {
        if (this._variants === undefined) {
          const source = this.querySelector('[type="application/json"]');
          try {
            this._variants = source ? JSON.parse(source.textContent) : null;
          } catch (error) {
            this._variants = null;
          }
        }
        return this._variants;
      }

      /* ------------------------------------------------------------------ */
      /* Visibility                                                          */
      /* ------------------------------------------------------------------ */

      watchVisibility() {
        // In the theme editor, keep the bar up while the section is selected so
        // settings can actually be seen taking effect.
        if (window.Shopify?.designMode) {
          document.addEventListener('shopify:section:select', (event) => {
            if (event.detail.sectionId === this.dataset.sectionId) this.show();
          });
          document.addEventListener('shopify:section:deselect', (event) => {
            if (event.detail.sectionId === this.dataset.sectionId) this.update();
          });
        }

        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              // Above the viewport means the customer has scrolled past the real
              // button; below means they have not reached it yet.
              this.scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
              this.update();
            });
          },
          { rootMargin: '0px 0px 0px 0px', threshold: 0 }
        );

        observer.observe(this.buttonWrapper);
        this.observers = [...(this.observers || []), observer];
      }

      update() {
        this.scrolledPast ? this.show() : this.hide();
      }

      show() {
        this.classList.add('is-visible');
        this.setAttribute('aria-hidden', 'false');

        // Publish the bar's real height so the chat launcher can lift clear of
        // it. Measured rather than hard-coded, since the height changes with
        // the merchant's content settings and with the safe-area inset.
        document.documentElement.style.setProperty('--sticky-atc-height', `${this.offsetHeight}px`);
        document.body.classList.add('sticky-atc-visible');
      }

      hide() {
        this.classList.remove('is-visible');
        this.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('sticky-atc-visible');
      }
    }
  );
}
